import React, { useMemo, useState } from "react";
import styled from "styled-components";
import { highlightHtml } from "../../utils/vocabHighlighter";

/**
 * 會「點單字跳出補充」的 HTML 區塊。
 *
 * 文章／詳解／逐字稿都經過這裡：先把單字表的字包上 .ce-vocab，
 * 再掛一個 delegate 的 click handler——點到 .ce-vocab 就在原地
 * 彈出字卡（老師寫的補充內容），點卡片外任何地方關閉。
 *
 * class 一律 ce- 前綴：這個站台載著 bootstrap 與 gel.css 兩套全域
 * 樣式，.card 這種名字會被搶走（dashboard 卡片踩過這個坑）。
 */

const StyledWrap = styled.div`
  position: relative;

  .ce-vocab {
    color: #b45309;
    border-bottom: 2px dotted #d97706;
    cursor: pointer;
    font-weight: 600;
  }
  .ce-vocab:hover {
    background: #fef3c7;
  }
`;

const StyledCard = styled.div`
  position: absolute;
  z-index: 30;
  left: ${(props) => props.$x}px;
  top: ${(props) => props.$y}px;
  width: 320px;
  max-width: 86vw;
  background: #fff;
  color: #1d2430;
  border: 1px solid #d0d7de;
  border-radius: 8px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
  text-align: left; /* 全站 .App 是置中，卡片內容要拉回靠左 */

  .ce-vocab-card-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 10px 14px;
    border-bottom: 1px solid #eee;
    font-weight: 700;
    font-size: 16px;
  }
  .ce-vocab-card-close {
    border: 0;
    background: none;
    font-size: 16px;
    cursor: pointer;
    color: #888;
    padding: 2px 6px;
  }
  .ce-vocab-card-body {
    padding: 12px 14px;
    font-size: 14px;
    line-height: 1.7;
    max-height: 320px;
    overflow-y: auto;
  }
  .ce-vocab-card-body p {
    margin: 0 0 8px;
  }
`;

const HtmlWithVocab = ({ html, vocabularies }) => {
  const [card, setCard] = useState(null); // { word, html, x, y }

  const vocabWords = useMemo(
    () => (vocabularies || []).map((v) => v.word),
    [vocabularies]
  );
  const vocabByWord = useMemo(() => {
    const map = {};
    (vocabularies || []).forEach((v) => {
      map[String(v.word).toLowerCase()] = v;
    });
    return map;
  }, [vocabularies]);

  const rendered = useMemo(
    () => highlightHtml(html, vocabWords),
    [html, vocabWords]
  );

  const handleClick = (event) => {
    const target = event.target.closest ? event.target.closest(".ce-vocab") : null;
    if (!target) {
      setCard(null);
      return;
    }
    const word = target.getAttribute("data-word") || "";
    const entry = vocabByWord[word.toLowerCase()];
    if (!entry) {
      return;
    }
    // 卡片開在被點的字下面（座標相對於外層容器），太靠右就往左收
    const wrap = event.currentTarget.getBoundingClientRect();
    const spot = target.getBoundingClientRect();
    const x = Math.max(0, Math.min(spot.left - wrap.left, wrap.width - 330));
    const y = spot.bottom - wrap.top + 6;
    setCard({ word: entry.word, html: entry.content_html || "", x, y });
  };

  return (
    <StyledWrap onClick={handleClick}>
      {/* eslint-disable-next-line react/no-danger */}
      <div dangerouslySetInnerHTML={{ __html: rendered }} />
      {card && (
        <StyledCard $x={card.x} $y={card.y} onClick={(e) => e.stopPropagation()}>
          <div className="ce-vocab-card-head">
            <span>{card.word}</span>
            <button
              type="button"
              className="ce-vocab-card-close"
              onClick={() => setCard(null)}
            >
              ✕
            </button>
          </div>
          <div
            className="ce-vocab-card-body"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: card.html || "<p>（這個字還沒有補充內容）</p>",
            }}
          />
        </StyledCard>
      )}
    </StyledWrap>
  );
};

export default HtmlWithVocab;
